import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { dashboardAPI } from '../services/api';

const buildCurrentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (monthValue) => {
  const [year, month] = monthValue.split('-').map(Number);
  const date = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(date);
};

const DEFAULT_HELPER_QUESTIONS = [
  'How much did I spend this month?',
  'How much did I spend this month vs last month?',
  'What are my top categories this month?',
  'What are my top stores in the last 3 months?',
  'How much did I spend on groceries this month?',
];
const ADVANCED_HELPER_QUESTIONS = [
  'Which category increased the most compared to last month?',
  'Which stores are driving most of my spending this month?',
  'What spending area should I reduce first to save 10% next month?',
  'Do I have any unusual spending pattern this month?',
  'What is one actionable change based on my last 3 months of spending?',
];
const AI_CHAT_STORAGE_PREFIX = 'dashboard-ai-chat-v1';
const MAX_STORED_TURNS = 40;
const TOOL_TO_DASHBOARD_FOCUS = {
  get_total_spend: 'key-insights',
  compare_previous_month: 'month-over-month',
  get_category_compare_previous_month: 'month-over-month',
  get_merchant_compare_previous_month: 'month-over-month',
  get_top_categories: 'spending-by-category',
  get_top_merchants: 'recent-transactions',
  get_category_spend: 'spending-by-category',
  get_merchant_spend: 'recent-transactions',
  get_actionable_guidance: 'spending-by-category',
};

function DashboardAssistant() {
  const location = useLocation();
  const autoAskedRef = useRef(false);
  const conversationPaneRef = useRef(null);
  const conversationEndRef = useRef(null);
  const [conversationHydrated, setConversationHydrated] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiConversation, setAiConversation] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [pendingQuestion, setPendingQuestion] = useState('');
  const [streamingAnswer, setStreamingAnswer] = useState('');
  const [streamStatus, setStreamStatus] = useState('');

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const monthParam = String(searchParams.get('month') || '').trim();
  const selectedMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(monthParam) ? monthParam : buildCurrentMonthValue();
  const seededQuestion = String(searchParams.get('q') || '').trim();
  const [selectedYear, selectedMonthNumber] = useMemo(() => selectedMonth.split('-').map(Number), [selectedMonth]);
  const getCitationLink = useCallback((citation) => {
    const tool = String(citation?.tool || '').trim();
    const focus = TOOL_TO_DASHBOARD_FOCUS[tool];
    const params = new URLSearchParams({ month: selectedMonth });
    if (focus) {
      params.set('focus', focus);
    }
    return `/dashboard?${params.toString()}`;
  }, [selectedMonth]);
  const chatStorageKey = useMemo(() => {
    let userId = 'anonymous';
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const parsedUser = JSON.parse(rawUser);
        userId = String(parsedUser?.id || parsedUser?.email || userId).trim() || userId;
      }
    } catch (error) {
      userId = 'anonymous';
    }
    return `${AI_CHAT_STORAGE_PREFIX}:${userId}:${selectedMonth}`;
  }, [selectedMonth]);
  const askedQuestions = useMemo(() => (
    new Set(aiConversation.map((turn) => String(turn.question || '').trim().toLowerCase()))
  ), [aiConversation]);
  const remainingHelperQuestions = useMemo(() => {
    return DEFAULT_HELPER_QUESTIONS.filter(
      (question) => !askedQuestions.has(String(question || '').trim().toLowerCase())
    );
  }, [askedQuestions]);
  const remainingAdvancedQuestions = useMemo(() => {
    return ADVANCED_HELPER_QUESTIONS.filter(
      (question) => !askedQuestions.has(String(question || '').trim().toLowerCase())
    );
  }, [askedQuestions]);

  const askQuestion = useCallback(async (questionText) => {
    const trimmedQuestion = String(questionText || '').trim();
    if (!trimmedQuestion || aiLoading) {
      return;
    }

    try {
      setAiLoading(true);
      setAiError('');
      setPendingQuestion(trimmedQuestion);
      setStreamingAnswer('');
      setStreamStatus('Planning answer...');
      let streamedAnswerBuffer = '';
      const conversationContext = aiConversation.slice(-6).map((turn) => ({
        question: turn.question,
        answer: turn.answer,
        intent: turn.intent,
        timeframe: turn.timeframeKey,
        targetName: turn.targetName,
        monthsBack: turn.monthsBack,
      }));
      const result = await dashboardAPI.askStream({
        question: trimmedQuestion,
        month: selectedMonthNumber,
        year: selectedYear,
        conversation: conversationContext,
        onEvent: (event) => {
          if (event?.type === 'status') {
            setStreamStatus(String(event.message || 'Thinking...'));
            return;
          }
          if (event?.type === 'answer_delta') {
            const delta = String(event.text || '');
            streamedAnswerBuffer += delta;
            setStreamingAnswer((prev) => `${prev}${delta}`);
          }
        },
      });
      const answerText = String(result?.answer || streamedAnswerBuffer || 'No answer available.');
      setAiConversation((prev) => [...prev, {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        question: trimmedQuestion,
        answer: answerText,
        intent: result?.intent || '',
        timeframeKey: result?.timeframe?.key || '',
        timeframeLabel: result?.timeframe?.label || '',
        targetName: result?.targetName || '',
        monthsBack: Number.isFinite(Number(result?.monthsBack)) ? Number(result.monthsBack) : undefined,
        toolCalls: Array.isArray(result?.toolCalls) ? result.toolCalls : [],
        toolCitations: Array.isArray(result?.toolCitations) ? result.toolCitations : [],
      }].slice(-MAX_STORED_TURNS));
      setAiQuestion('');
      setPendingQuestion('');
      setStreamingAnswer('');
      setStreamStatus('');
    } catch (requestError) {
      console.error('Error asking dashboard question:', requestError);
      setAiError('Could not get an answer right now. Please try again.');
      setPendingQuestion('');
      setStreamingAnswer('');
      setStreamStatus('');
    } finally {
      setAiLoading(false);
    }
  }, [aiConversation, aiLoading, selectedMonthNumber, selectedYear]);

  const handleAskDashboardQuestion = useCallback(async (event) => {
    event.preventDefault();
    await askQuestion(aiQuestion);
  }, [aiQuestion, askQuestion]);

  useEffect(() => {
    setConversationHydrated(false);
    autoAskedRef.current = false;
  }, [chatStorageKey]);

  useEffect(() => {
    let restoredConversation = [];
    try {
      const raw = localStorage.getItem(chatStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          restoredConversation = parsed
            .filter((turn) => turn && typeof turn === 'object')
            .map((turn) => ({
              id: String(turn.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
              question: String(turn.question || ''),
              answer: String(turn.answer || ''),
              intent: String(turn.intent || ''),
              timeframeKey: String(turn.timeframeKey || ''),
              timeframeLabel: String(turn.timeframeLabel || ''),
              targetName: String(turn.targetName || ''),
              monthsBack: Number.isFinite(Number(turn.monthsBack)) ? Number(turn.monthsBack) : undefined,
              toolCalls: Array.isArray(turn.toolCalls) ? turn.toolCalls : [],
              toolCitations: Array.isArray(turn.toolCitations) ? turn.toolCitations : [],
            }))
            .slice(-MAX_STORED_TURNS);
        }
      }
    } catch (error) {
      restoredConversation = [];
    }

    setAiConversation(restoredConversation);
    setConversationHydrated(true);
  }, [chatStorageKey]);

  useEffect(() => {
    if (!conversationHydrated) {
      return;
    }

    try {
      localStorage.setItem(chatStorageKey, JSON.stringify(aiConversation.slice(-MAX_STORED_TURNS)));
    } catch (error) {
      // Ignore storage failures (quota/private mode) and keep session functional.
    }
  }, [aiConversation, chatStorageKey, conversationHydrated]);

  useEffect(() => {
    if (!seededQuestion || autoAskedRef.current) {
      return;
    }
    if (!conversationHydrated) {
      return;
    }
    if (aiConversation.some((turn) => String(turn.question || '').trim().toLowerCase() === seededQuestion.toLowerCase())) {
      autoAskedRef.current = true;
      return;
    }

    autoAskedRef.current = true;
    askQuestion(seededQuestion);
  }, [aiConversation, askQuestion, conversationHydrated, seededQuestion]);

  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }

    if (conversationPaneRef.current) {
      conversationPaneRef.current.scrollTop = conversationPaneRef.current.scrollHeight;
    }
  }, [aiConversation, aiLoading, pendingQuestion]);

  return (
    <div className="mx-auto flex h-[calc(100vh-9rem)] w-full max-w-4xl flex-col">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">Spending AI</h2>
          <p className="text-sm text-slate-500">{formatMonthLabel(selectedMonth)} context</p>
        </div>
        <Link to={`/dashboard?month=${selectedMonth}`} className="text-sm font-medium text-blue-700 hover:text-blue-800">
          Back
        </Link>
      </div>

      <div ref={conversationPaneRef} className="flex-1 overflow-y-auto pr-1">
        {aiError && (
          <p className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {aiError}
          </p>
        )}

        {aiConversation.length === 0 && (
          <div className="mx-auto mt-14 max-w-2xl rounded-2xl border border-slate-200 bg-white px-5 py-6 text-center">
            <p className="text-base font-semibold text-slate-900">Ask anything about your spending</p>
            <p className="mt-2 text-sm text-slate-500">Try totals, category trends, store comparisons, or month-over-month changes.</p>
          </div>
        )}

        <div className="space-y-4 pb-5 pt-2">
          {aiConversation.slice(-10).map((turn) => (
            <div key={turn.id} className="space-y-2">
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl bg-slate-800 px-4 py-2.5 text-sm text-white shadow-sm">
                  {turn.question}
                </div>
              </div>
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 shadow-sm">
                  {turn.answer}
                  {Array.isArray(turn.toolCitations) && turn.toolCitations.length > 0 && (
                    <div className="mt-2 border-t border-slate-100 pt-2">
                      <p className="text-xs text-slate-500">Based on:</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {turn.toolCitations.map((citation, index) => (
                          <Link
                            key={`${turn.id}-citation-${String(citation?.tool || 'tool')}-${index}`}
                            to={getCitationLink(citation)}
                            className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 transition hover:bg-slate-100"
                            title="Open related dashboard section"
                          >
                            {`${citation?.label || citation?.tool || 'Tool'} (${citation?.timeframeLabel || 'selected period'})`}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {pendingQuestion && aiLoading && (
            <div className="space-y-2">
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl bg-slate-800 px-4 py-2.5 text-sm text-white shadow-sm">
                  {pendingQuestion}
                </div>
              </div>
              <div className="flex justify-start">
                {streamingAnswer ? (
                  <div className="max-w-[85%] rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 shadow-sm">
                    {streamingAnswer}
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-sm">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.2s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.1s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
                  </div>
                )}
              </div>
              {streamStatus && (
                <p className="pl-1 text-xs text-slate-500">{streamStatus}</p>
              )}
            </div>
          )}
          <div ref={conversationEndRef} />
        </div>

        {remainingHelperQuestions.length > 0 && (
          <div className="mb-3 mt-2">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Try a question</p>
            <div className="flex flex-wrap gap-2">
              {remainingHelperQuestions.map((helperQuestion) => (
                <button
                  key={helperQuestion}
                  type="button"
                  onClick={() => askQuestion(helperQuestion)}
                  disabled={aiLoading}
                  className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {helperQuestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {remainingAdvancedQuestions.length > 0 && (
          <div className="mb-3 mt-2">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">More insightful questions</p>
            <div className="flex flex-wrap gap-2">
              {remainingAdvancedQuestions.map((helperQuestion) => (
                <button
                  key={helperQuestion}
                  type="button"
                  onClick={() => askQuestion(helperQuestion)}
                  disabled={aiLoading}
                  className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {helperQuestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 z-20 border-t border-slate-200/70 bg-gradient-to-t from-slate-100/95 via-slate-100/70 to-transparent pt-3">
        <form
          onSubmit={handleAskDashboardQuestion}
          className="rounded-2xl border border-slate-200 bg-white p-2 shadow-lg"
        >
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={aiQuestion}
              onChange={(event) => setAiQuestion(event.target.value)}
              placeholder="Message Spending AI..."
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            <button
              type="submit"
              disabled={aiLoading || !String(aiQuestion || '').trim()}
              aria-label={aiLoading ? 'Thinking' : 'Send question'}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M10 17a1 1 0 01-1-1V7.414L6.707 9.707a1 1 0 11-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 11-1.414 1.414L11 7.414V16a1 1 0 01-1 1z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DashboardAssistant;
